/**
 * PublicRequisitesPage - Публичная страница заполнения реквизитов
 *
 * Доступна без авторизации по ссылке /public/requisites/:token.
 * Не использует SessionContext, Header, Sidebar.
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Center,
  Loader,
  Stack,
  Alert,
  Box,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCheck,
  IconAlertTriangle,
  IconLock,
  IconClockOff,
  IconBuildingBank,
} from '@tabler/icons-react';
import PublicRequisitesForm from '../components/Public/PublicRequisitesForm';

/**
 * Состояния страницы: loading, error, form, success
 */
const PublicRequisitesPage = () => {
  const { token } = useParams();

  const [pageState, setPageState] = useState('loading'); // loading | error | form | success
  const [organizationName, setOrganizationName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState(''); // not_found | filled | expired | invalidated
  const [submitting, setSubmitting] = useState(false);

  // Загрузка данных ссылки
  useEffect(() => {
    const fetchLinkData = async () => {
      try {
        // Считаем визит только при первом открытии вкладки.
        // sessionStorage очищается при закрытии вкладки — новая вкладка = новый визит.
        // Рефреш (F5) и React StrictMode double-mount НЕ считаются.
        const sessionKey = `requisites_visited_${token}`;
        const isNewVisit = !sessionStorage.getItem(sessionKey);
        const url = isNewVisit
          ? `/api/public/requisites/${token}?count_visit=true`
          : `/api/public/requisites/${token}`;

        const response = await fetch(url);

        // Ставим метку ПОСЛЕ успешного запроса
        if (response.ok && isNewVisit) {
          sessionStorage.setItem(sessionKey, '1');
        }

        if (!response.ok) {
          if (response.status === 404) {
            setErrorType('not_found');
            setErrorMessage('Ссылка не найдена или недействительна');
          } else {
            const data = await response.json();
            setErrorMessage(data.detail || 'Произошла ошибка');
          }
          setPageState('error');
          return;
        }

        const data = await response.json();
        setOrganizationName(data.organization_name);

        if (!data.is_active) {
          // Ссылка неактивна
          setErrorType(data.status);
          const statusMessages = {
            filled: 'Реквизиты уже были заполнены по этой ссылке',
            expired: 'Срок действия ссылки истёк',
            invalidated: 'Ссылка была отменена',
          };
          setErrorMessage(
            statusMessages[data.status] || 'Ссылка больше недействительна'
          );
          setPageState('error');
          return;
        }

        setPageState('form');
      } catch (err) {
        setErrorMessage('Не удалось загрузить данные. Попробуйте позже.');
        setPageState('error');
      }
    };

    if (token) {
      fetchLinkData();
    }
  }, [token]);

  // Отправка формы
  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      const response = await fetch(`/api/public/requisites/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при отправке данных');
      }

      setPageState('success');
    } catch (err) {
      throw err; // Пробросить для отображения в форме
    } finally {
      setSubmitting(false);
    }
  };

  // Иконка ошибки в зависимости от типа
  const getErrorIcon = () => {
    switch (errorType) {
      case 'filled':
        return <IconCheck size={48} />;
      case 'expired':
        return <IconClockOff size={48} />;
      case 'invalidated':
        return <IconLock size={48} />;
      default:
        return <IconAlertTriangle size={48} />;
    }
  };

  const getErrorColor = () => {
    switch (errorType) {
      case 'filled':
        return 'blue';
      case 'expired':
        return 'orange';
      case 'invalidated':
        return 'red';
      default:
        return 'gray';
    }
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        paddingTop: 40,
        paddingBottom: 40,
      }}
    >
      <Container size="sm">
        {/* Загрузка */}
        {pageState === 'loading' && (
          <Center py={100}>
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text c="dimmed">Загрузка...</Text>
            </Stack>
          </Center>
        )}

        {/* Ошибка */}
        {pageState === 'error' && (
          <Paper radius="xl" p={40} shadow="sm">
            <Center py="xl">
              <Stack align="center" gap="lg">
                <ThemeIcon
                  size={80}
                  radius="xl"
                  color={getErrorColor()}
                  variant="light"
                >
                  {getErrorIcon()}
                </ThemeIcon>
                <Title order={3} ta="center">
                  {errorType === 'filled'
                    ? 'Реквизиты уже заполнены'
                    : errorType === 'expired'
                    ? 'Ссылка истекла'
                    : errorType === 'invalidated'
                    ? 'Ссылка отменена'
                    : 'Ссылка недоступна'}
                </Title>
                <Text c="dimmed" ta="center" maw={400}>
                  {errorMessage}
                </Text>
                {organizationName && (
                  <>
                    <Text size="sm" c="dimmed">
                      Организация: {organizationName}
                    </Text>
                    <Text size="xs" c="dimmed" mt="sm">
                      ООО &laquo;Это Интересно — Москва&raquo;
                    </Text>
                  </>
                )}
              </Stack>
            </Center>
          </Paper>
        )}

        {/* Форма */}
        {pageState === 'form' && (
          <Paper radius="xl" p={40} shadow="sm">
            <Stack gap="lg">
              {/* Заголовок */}
              <Center>
                <Stack align="center" gap="xs">
                  <ThemeIcon
                    size={56}
                    radius="xl"
                    color="blue"
                    variant="light"
                  >
                    <IconBuildingBank size={28} />
                  </ThemeIcon>
                  <Title order={3} ta="center">
                    Заполнение реквизитов
                  </Title>
                  <Text c="dimmed" ta="center">
                    Для организации: <strong>{organizationName}</strong>
                  </Text>
                </Stack>
              </Center>

              <Alert
                variant="light"
                color="blue"
                radius="xl"
              >
                Пожалуйста, заполните юридические и банковские реквизиты вашей организации.
                Данные будут переданы в систему управления партнёрством.
              </Alert>

              {/* Форма реквизитов */}
              <PublicRequisitesForm
                onSubmit={handleSubmit}
                loading={submitting}
              />
            </Stack>
          </Paper>
        )}

        {/* Успех */}
        {pageState === 'success' && (
          <Paper radius="xl" p={40} shadow="sm">
            <Center py="xl">
              <Stack align="center" gap="lg">
                <ThemeIcon
                  size={80}
                  radius="xl"
                  color="green"
                  variant="light"
                >
                  <IconCheck size={48} />
                </ThemeIcon>
                <Title order={3} ta="center">
                  Спасибо!
                </Title>
                <Text c="dimmed" ta="center" maw={400}>
                  Реквизиты успешно отправлены. Данные были переданы ответственному сотруднику.
                </Text>
                {organizationName && (
                  <Text size="sm" c="dimmed">
                    Организация: {organizationName}
                  </Text>
                )}
                <Text size="xs" c="dimmed" mt="sm">
                  ООО &laquo;Это Интересно — Москва&raquo;
                </Text>
              </Stack>
            </Center>
          </Paper>
        )}

        {/* Футер (только для формы) */}
        {pageState === 'form' && (
          <Text c="dimmed" size="xs" ta="center" mt="lg">
            ООО &laquo;Это Интересно — Москва&raquo;
          </Text>
        )}
      </Container>
    </Box>
  );
};

export default PublicRequisitesPage;
